import { Icon } from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { styled } from '@mui/material/styles';
import React, { useState, useEffect } from 'react';
import { ProcessStep, WOSTATUS } from '../../common/enums/enums';
import {
  PendingApprovalIcon,
  PauseIcon,
  ApprovedIcon,
  InTransitIcon,
  InProcessIcon,
  DoneIcon,
  DeclinedIcon,
  ArrivedIcon,
} from '../Icons/Services';

const STATUS_STEPS = [
  {
    backgroundColor: '#FFFFFF',
    color: '#1E1E1E',
    hoverBgColor: '#B2B2B2',
    hoverColor: '#1E1E1E',
    activeBgColor: '#858585',
    activeColor: '#FFFFFF',
    StatusIcon: null,
    text: WOSTATUS.ALL,
    orderStatus: WOSTATUS.ALL,
  },
  {
    backgroundColor: '#FFFFFF',
    color: '#EB8C00',
    hoverBgColor: '#FFDFAE',
    hoverColor: '#EB8C00',
    activeBgColor: '#EB8C00',
    activeColor: '#FFFFFF',
    StatusIcon: <PendingApprovalIcon fontSize="small" />,
    text: ProcessStep.PENDING_APPROVAL,
    orderStatus: ProcessStep.GENERATED,
  },
  {
    backgroundColor: '#FFFFFF',
    color: '#4CAF50',
    hoverBgColor: '#C6FFC8',
    hoverColor: '#4CAF50',
    activeBgColor: '#4CAF50',
    activeColor: '#F3F3F3',
    StatusIcon: <ApprovedIcon />,
    text: ProcessStep.APPROVED,
    orderStatus: ProcessStep.APPROVED,
  },
  {
    backgroundColor: '#FFFFFF',
    color: '#0597E0',
    hoverBgColor: '#D4F1FF',
    hoverColor: '#0597E0',
    activeBgColor: '#0597E0',
    activeColor: '#F3F3F3',
    StatusIcon: <InTransitIcon color={'#fff'} backgroundColor={'#0597E0'} />,
    text: WOSTATUS.IN_TRANSIT,
    orderStatus: WOSTATUS.IN_TRANSIT,
  },
  {
    backgroundColor: '#FFFFFF',
    color: '#0057B2',
    hoverBgColor: '#BADCFF',
    hoverColor: '#0057B2',
    activeBgColor: '#0057B2',
    activeColor: '#F3F3F3',
    StatusIcon: <ArrivedIcon color="#FFFFFF" backgroundColor={'#0057B2'} />,
    text: ProcessStep.ARRIVED,
    orderStatus: ProcessStep.ARRIVED,
  },
  {
    backgroundColor: '#FFFFFF',
    color: '#FF9800',
    hoverBgColor: '#FFE6C1',
    hoverColor: '#FF9800',
    activeBgColor: '#FF9800',
    activeColor: '#F3F3F3',
    StatusIcon: <InProcessIcon color="#FFFFFF" backgroundColor={'#FF9800'} />,
    text: WOSTATUS.IN_PROCESS,
    orderStatus: WOSTATUS.IN_PROCESS,
  },
  {
    backgroundColor: '#FFFFFF',
    color: '#4CAF50',
    hoverBgColor: '#CAFFCC',
    hoverColor: '#4CAF50',
    activeBgColor: '#4CAF50',
    activeColor: '#F3F3F3',
    StatusIcon: <DoneIcon fontSize="small" />,
    text: ProcessStep.DONE,
    orderStatus: ProcessStep.DONE,
  },
  {
    backgroundColor: '#FFFFFF',
    color: '#FF3E3E',
    hoverBgColor: '#FFB9B9',
    hoverColor: '#FF3E3E',
    activeBgColor: '#FF3E3E',
    activeColor: '#F3F3F3',
    StatusIcon: (
      <DeclinedIcon testId="CanceledIcon" backgroundColor="#FF3E3E" />
    ),
    text: ProcessStep.CANCELED,
    orderStatus: ProcessStep.CANCELED,
  },
  {
    backgroundColor: '#FFFFFF',
    color: '#FF3E3E',
    hoverBgColor: '#FFB9B9',
    hoverColor: '#FF3E3E',
    activeBgColor: '#FF3E3E',
    activeColor: '#F3F3F3',
    StatusIcon: <DeclinedIcon backgroundColor="#FF3E3E" />,
    text: ProcessStep.DECLINED,
    orderStatus: ProcessStep.DECLINED,
  },

  {
    backgroundColor: '#FFFFFF',
    color: '#FF3E3E',
    hoverBgColor: '#FFB9B9',
    hoverColor: '#FF3E3E',
    activeBgColor: '#FF3E3E',
    activeColor: '#F3F3F3',
    StatusIcon: <PauseIcon />,
    text: ProcessStep.PAUSED,
    orderStatus: ProcessStep.PAUSED,
  },
];

const STYLE_PROPS = new Set([
  'textColor',
  'backgroundColor',
  'hoverBgColor',
  'hoverColor',
  'activeBgColor',
  'activeColor',
]);

const StyledFormControlLabel = styled(FormControlLabel, {
  shouldForwardProp: prop => !STYLE_PROPS.has(prop),
})(
  ({
    textColor,
    backgroundColor,
    hoverBgColor,
    hoverColor,
    activeBgColor,
    activeColor,
  }) => ({
    padding: '4px 16px',
    border: 'none',
    borderRadius: '50px',
    background: backgroundColor,
    color: textColor,
    maxHeight: '32px',
    [`.MuiSvgIcon-root`]: {
      fontSize: '18px',
    },
    [`.MuiIcon-root`]: {
      fontSize: '18px',

      [`svg`]: {
        height: '18px',
      },
    },
    [`.MuiFormControlLabel-label`]: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
      lineHeight: '24px',
    },
    [`.MuiRadio-root`]: {
      display: 'none',
    },
    [`&:hover`]: {
      background: `linear-gradient(#fff, ${hoverBgColor})`,
      color: hoverColor,
    },
    [`&.checked`]: {
      background: activeBgColor,
      color: activeColor,

      [`[data-testid="InTransitIcon"]`]: {
        [`circle`]: {
          fill: activeColor,
        },
        [`path`]: {
          fill: activeBgColor,
        },
      },
      [`[data-testid="ArrivedIcon"]`]: {
        [`circle`]: {
          fill: activeColor,
        },
        [`path`]: {
          fill: activeBgColor,
        },
      },
      [`[data-testid="InProcessIcon"]`]: {
        [`circle`]: {
          fill: activeColor,
        },
        [`path`]: {
          fill: activeBgColor,
        },
      },
      [`[data-testid="DeclinedIcon"]`]: {
        [`path`]: {
          fill: activeColor,
        },
      },
      [`[data-testid="CanceledIcon"]`]: {
        [`path`]: {
          fill: activeColor,
        },
      },
    },
  })
);

const StyledContainer = styled('div')`
  padding-left: 25px;
  width: 100%;
`;

const StyledRadioGroup = styled(RadioGroup)`
  row-gap: 1rem;
  column-gap: 0.75rem;
  flex-grow: 1;
`;

export default function WOStatusComponent({
  availableOrderStatus = [],
  setSelectedStatus,
}) {
  const [value, setValue] = useState(WOSTATUS.ALL);
  const availableStatus = STATUS_STEPS.slice(1).filter(item =>
    availableOrderStatus?.some(
      step => item.orderStatus.toLowerCase() === step?.toLowerCase()
    )
  );
  const orderStatus =
    availableOrderStatus.length > 0
      ? [STATUS_STEPS[0], ...availableStatus]
      : STATUS_STEPS;

  const handleChange = event => {
    setValue(event.target.value);
    setSelectedStatus(event.target.value);
  };

  useEffect(() => {
    setValue(WOSTATUS.ALL);
    setSelectedStatus(WOSTATUS.ALL);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableOrderStatus]);

  return (
    <StyledContainer>
      <StyledRadioGroup
        defaultValue="All"
        name="status-radio-btns"
        row
        value={value}
        onChange={handleChange}
      >
        {orderStatus.map(item => {
          const {
            StatusIcon,
            text,
            backgroundColor,
            color,
            activeBgColor,
            activeColor,
            hoverBgColor,
            hoverColor,
            orderStatus: status,
          } = item;
          const IconLabel = () => (
            <>
              <Icon fontSize="small">{StatusIcon}</Icon>
              <span>{text}</span>
            </>
          );
          const label = StatusIcon ? <IconLabel /> : text;
          return (
            <StyledFormControlLabel
              key={status}
              textColor={color}
              backgroundColor={backgroundColor}
              hoverBgColor={hoverBgColor}
              hoverColor={hoverColor}
              activeColor={activeColor}
              className={value === status ? 'checked' : null}
              activeBgColor={activeBgColor}
              checked={value === status}
              value={status}
              control={<Radio />}
              label={label}
            />
          );
        })}
      </StyledRadioGroup>
    </StyledContainer>
  );
}
